import { useTranslation } from "react-i18next";
import "./UserIntakeModule.scss";
import { useState, useEffect } from "react";
import useSendEmail from "../../hooks/useSendEmail";
import { useScanImage } from "../../context/ImageContext";
import { UpdateUserInfoUrl } from "../../constants";
import { useConfig } from "../../context/ConfigContext";
import { InfoCollection } from "./InfoCollection/InfoCollection";
import FaceMeshAnimation from "../animations/FaceMeshAnimation/FaceMeshAnimation";
import { useUID } from "../../context/UIDContext";
import { useAmplitude } from "../../context/AmplitudeContext";
import { useTracker } from "../../context/TrackerContext";

export const UserIntakeModule = ({
  apiToken,
  fetchAIResults,
  runNextModule,
  onError,
}) => {
  const { i18n } = useTranslation("userIntakeModule");
  const [currentStep, setCurrentStep] = useState(0);
  const { uid } = useUID();
  const trackEvent = useAmplitude();
  const { itrack } = useTracker();

  const { addUserEmail, sendEmail } = useSendEmail();
  const { frontAnimationImage, uploadImages } = useScanImage();

  const [fetchResultsComplete, setFetchResultsComplete] = useState(false);
  const [isResultResponseSuccess, setIsResultResponseSuccess] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isSaveDone, setIsSaveDone] = useState(false);
  const { configData, userQuestions, fetchQuestionsData } = useConfig();

  const [imagesUploaded, setImageUploaded] = useState(false);
  const [questionsFetched, setQuestionsFetched] = useState(false);

  const fetchResults = async () => {
    const resResponse = await fetchAIResults();
    if (resResponse) {
      setIsResultResponseSuccess(true);
    } else {
      setIsResultResponseSuccess(false);
    }
    setFetchResultsComplete(true);
  };

  useEffect(() => {
    if (!imagesUploaded) {
      uploadImages(apiToken).then((res) => {
        if (res) {
          itrack({
            eventName: "scan_images_saved",
            trackerId: uid,
          });
          trackEvent("webapp_user_scan_images_saved", uid);
          setImageUploaded(true);
        }
      });
    }
  }, [apiToken, imagesUploaded, uploadImages, uid]);

  useEffect(() => {
    if (!questionsFetched) {
      fetchQuestionsData().then((res) => {
        if (res) {
          setQuestionsFetched(true);
        }
      });
    }
  }, [questionsFetched, fetchQuestionsData]);

  useEffect(() => {
    if (questionsFetched && imagesUploaded) {
      fetchResults();
      const timerId = setTimeout(async () => {
        if (
          configData.QuestionsModuleFlags.emailEnabled ||
          userQuestions.length > 0
        ) {
          setCurrentStep(1);
        } else {
          setCurrentStep(2);
          setIsEmailSent(true);
          saveSelectedInfo({ lang: i18n.language });
        }
      }, 1500);
      return () => clearTimeout(timerId);
    }
  }, [questionsFetched, imagesUploaded]);

  useEffect(() => {
    if (currentStep === 2 && fetchResultsComplete && !isResultResponseSuccess) {
      onError();
    }
    if (
      currentStep === 2 &&
      fetchResultsComplete &&
      isResultResponseSuccess &&
      isSaveDone &&
      isEmailSent
    ) {
      const timerId = setTimeout(async () => {
        runNextModule();
      }, 1700);
      return () => clearTimeout(timerId);
    }
  }, [
    currentStep,
    fetchResultsComplete,
    isResultResponseSuccess,
    isSaveDone,
    isEmailSent,
    onError,
  ]);

  useEffect(() => {
    if (
      currentStep === 2 &&
      fetchResultsComplete &&
      isResultResponseSuccess &&
      !isEmailSent
    ) {
      if (configData.QuestionsModuleFlags.emailEnabled)
        sendEmail(i18n.language, apiToken);
      setIsEmailSent(true);
    }
  }, [
    i18n.language,
    apiToken,
    isEmailSent,
    currentStep,
    configData.QuestionsModuleFlags.emailEnabled,
    fetchResultsComplete,
    isResultResponseSuccess,
    sendEmail,
  ]);

  const saveSelectedInfo = async (userData) => {
    try {
      const res = await fetch(UpdateUserInfoUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: apiToken,
        },
        body: JSON.stringify(userData),
      });
      if (!res.ok) {
        throw new Error("Unable to send Questionnaire data!");
      }
    } catch (error) {
      console.error("Error saving selected info:", error);
    }
    setIsSaveDone(true);
  };

  return (
    <div className="user-intake-module">
      <div className="background-container">
        <FaceMeshAnimation
          imageUrl={frontAnimationImage.imageUrl || ""}
          keypoints={frontAnimationImage.keypoints || []}
          width={frontAnimationImage.width || 2000}
          height={frontAnimationImage.height || 3000}
        />
        <div className="background-image-overlay" />
      </div>
      {currentStep === 1 && (
        <InfoCollection
          emailEnabled={configData.QuestionsModuleFlags.emailEnabled}
          updateEmail={addUserEmail}
          questionsData={userQuestions}
          onComplete={(data) => {
            setCurrentStep(2);
            saveSelectedInfo(data);
          }}
        />
      )}
    </div>
  );
};
